import dayjs from 'dayjs'
import store from '../../../../../../../store'
import { createDocument } from '../../../../Documents/utils/api'
import { blobToBase64, handleError } from '../../../../../../lib/utils'
import { documentsTypes } from '../../../../Documents/utils/enums'

/**
 * Generate HTML document and save to Documents
 * @param {string} content - The text content to save
 * @param {string} documentType - The type of document (user specified)
 * @param {Object} bookingData - Booking information
 * @param {string} userRef - User reference if no booking
 * @returns {Promise} - Resolves with the created document record
 */
export const saveContentToDocuments = async (content, documentType, bookingData, userRef) => {
  try {
    // Get current user and clinic information
    const authUser = store.getState().auth.agiliteUser
    let clinicDetails = null

    if (bookingData?.clinicRef) {
      clinicDetails = store.getState().clinics.data.find((clinic) => clinic._id === bookingData.clinicRef)
    } else {
      clinicDetails = store.getState().clinics.data.find((clinic) => authUser.extraData.clinics.includes(clinic._id))
    }

    // Get patient information
    const patientRecord = bookingData?.patientRecord || null

    // Prepare template data
    const templateData = {
      documentType,
      content: content.replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '').replace(/<[^>]*>/g, ''), // Clean HTML
      date: dayjs().format('DD MMM YYYY'),
      time: dayjs().format('HH:mm'),

      // Patient information
      patientName: patientRecord ? `${patientRecord.firstName} ${patientRecord.lastName}` : 'N/A',
      patientId: patientRecord?.idNo || 'N/A',
      patientPhone: patientRecord?.phoneNumber || 'N/A',

      // Medical professional information
      medProfName: `${authUser.firstName} ${authUser.lastName}`,
      qualification: authUser.extraData?.qualification || 'Medical Professional',

      // Clinic information
      practiceName: clinicDetails?.practiceName || 'N/A',
      practiceNo: clinicDetails?.practiceNumber || 'N/A',
      regNo: clinicDetails?.practiceRegNumber || 'N/A',
      clinicAddress: clinicDetails?.address || 'N/A',
      clinicPhone: clinicDetails?.phoneNumber || 'N/A',

      // Entity branding
      entityLogo: store.getState().core.entity.entityLogo?.split(',')[1] || '',
      entityIcon: store.getState().core.entity.entityIcon?.split(',')[1] || ''
    }

    // Generate HTML content
    const htmlContent = generateHTMLDocument(content, documentType, templateData)
    const blob = new Blob([htmlContent], { type: 'text/html' })
    const base64Data = await blobToBase64(blob)

    // Save to Documents using existing API with OTHER document type
    const documentName = `${documentType} (${dayjs().format('DD-MM-YYYY')})`

    const newRecord = await createDocument(
      base64Data,
      {
        name: documentName,
        userRef: bookingData?.userRef || userRef,
        bookingRef: bookingData?._id || ''
      },
      documentsTypes.OTHER // Use the OTHER document type from enums
    )

    return newRecord
  } catch (error) {
    throw new Error(handleError(error))
  }
}

/**
 * Generate HTML document for the content
 * @param {string} content - The content to include
 * @param {string} documentType - Document type
 * @param {Object} templateData - Template data
 * @returns {string} - HTML content
 */
const generateHTMLDocument = (content, documentType, templateData) => {
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${documentType}</title>
        <meta charset="UTF-8">
        <style>
          body { 
            font-family: Arial, sans-serif; 
            margin: 40px; 
            line-height: 1.6; 
            color: #333;
          }
          .header { 
            text-align: center; 
            margin-bottom: 30px; 
            border-bottom: 2px solid #f0f0f0;
            padding-bottom: 20px;
          }
          .title { 
            color: #1890ff; 
            margin-bottom: 10px; 
            font-size: 24px;
            font-weight: bold;
          }
          .subtitle {
            color: #666;
            font-size: 14px;
            margin-bottom: 5px;
          }
          .content { 
            white-space: pre-wrap; 
            margin: 20px 0;
            padding: 20px;
            background-color: #fafafa;
            border-radius: 8px;
            border: 1px solid #e0e0e0;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #f0f0f0;
            font-size: 12px;
            color: #666;
          }
          .patient-info, .clinic-info {
            display: inline-block;
            width: 48%;
            vertical-align: top;
          }
          .info-section {
            margin-bottom: 15px;
          }
          .info-label {
            font-weight: bold;
            color: #333;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 class="title">${documentType}</h1>
          <div class="subtitle">Generated on ${templateData.date} at ${templateData.time}</div>
          ${templateData.practiceName !== 'N/A' ? `<div class="subtitle">${templateData.practiceName}</div>` : ''}
        </div>

        ${
          templateData.patientName !== 'N/A'
            ? `
          <div style="margin-bottom: 30px;">
            <div class="patient-info">
              <div class="info-section">
                <span class="info-label">Patient:</span> ${templateData.patientName}
              </div>
              ${
                templateData.patientId !== 'N/A'
                  ? `
                <div class="info-section">
                  <span class="info-label">ID:</span> ${templateData.patientId}
                </div>
              `
                  : ''
              }
            </div>
            <div class="clinic-info">
              <div class="info-section">
                <span class="info-label">Prepared by:</span> ${templateData.medProfName}
              </div>
              ${
                templateData.qualification
                  ? `
                <div class="info-section">
                  <span class="info-label">Qualification:</span> ${templateData.qualification}
                </div>
              `
                  : ''
              }
            </div>
          </div>
        `
            : ''
        }

        <div class="content">${content}</div>

        <div class="footer">
          ${
            templateData.clinicAddress !== 'N/A'
              ? `
            <div><strong>Address:</strong> ${templateData.clinicAddress}</div>
          `
              : ''
          }
          ${
            templateData.clinicPhone !== 'N/A'
              ? `
            <div><strong>Phone:</strong> ${templateData.clinicPhone}</div>
          `
              : ''
          }
          ${
            templateData.practiceNo !== 'N/A'
              ? `
            <div><strong>Practice Number:</strong> ${templateData.practiceNo}</div>
          `
              : ''
          }
          ${
            templateData.regNo !== 'N/A'
              ? `
            <div><strong>Registration Number:</strong> ${templateData.regNo}</div>
          `
              : ''
          }
        </div>
      </body>
    </html>
  `
}
